import Box from '@mui/material/Box';
import React, { memo } from 'react';
import { useIntl } from 'react-intl';
import IconActivityAlpaca from '../Icons/Activities/IconActivityAlpaca';
import IconActivityClimbing from '../Icons/Activities/IconActivityClimbing';
import IconActivityCoworking from '../Icons/Activities/IconActivityCoworking';
import IconActivityCycling from '../Icons/Activities/IconActivityCycling';
import IconActivityFishing from '../Icons/Activities/IconActivityFishing';
import IconActivityHiking from '../Icons/Activities/IconActivityHiking';
import IconActivityPaddle from '../Icons/Activities/IconActivityPaddle';
import IconActivityQuadTour from '../Icons/Activities/IconActivityQuadTour';
import IconActivityRiding from '../Icons/Activities/IconActivityRiding';
import IconActivitySailing from '../Icons/Activities/IconActivitySailing';
import IconActivitySightseeingFlight from '../Icons/Activities/IconActivitySightseeingFlight';
import IconActivitySkydiving from '../Icons/Activities/IconActivitySkydiving';
import IconActivitySurfing from '../Icons/Activities/IconActivitySurfing';
import IconActivitySwimming from '../Icons/Activities/IconActivitySwimming';
import IconActivityTennis from '../Icons/Activities/IconActivityTennis';
import IconActivityWakeBoarding from '../Icons/Activities/IconActivityWakeBoarding';
import IconActivityWellness from '../Icons/Activities/IconActivityWellness';
import IconActivityWineTasting from '../Icons/Activities/IconActivityWineTasting';
import ItemChip from './ItemChip';

const getIcon = (optionKey) => {
  switch (optionKey) {
    case 'alpaca':
      return <IconActivityAlpaca />;
    case 'climbing':
      return <IconActivityClimbing />;
    case 'coworking':
      return <IconActivityCoworking />;
    case 'cycling':
      return <IconActivityCycling />;
    case 'fishing':
      return <IconActivityFishing />;
    case 'hiking':
      return <IconActivityHiking />;
    case 'paddle':
      return <IconActivityPaddle />;
    case 'quad_tour':
      return <IconActivityQuadTour />;
    case 'riding':
      return <IconActivityRiding />;
    case 'sailing':
      return <IconActivitySailing />;
    case 'sightseeing_flight':
      return <IconActivitySightseeingFlight />;
    case 'skydiving':
      return <IconActivitySkydiving />;
    case 'surfing':
      return <IconActivitySurfing />;
    case 'swimming':
      return <IconActivitySwimming />;
    case 'tennis':
      return <IconActivityTennis />;
    case 'wake_boarding':
      return <IconActivityWakeBoarding />;
    case 'wellness':
      return <IconActivityWellness />;
    case 'wine_tasting':
      return <IconActivityWineTasting />;
    default:
      return null;
  }
};

/**
 * ActivitiesPropertyGroup renders a list of options as chips, indicating selected and non-selected items.
 *
 * It's used for displaying a group of selectable properties, such as amenities or activities,
 * in a form where users can see their current selections at a glance.
 *
 * Props:
 *   - id (string): A unique identifier for the group of options.
 *   - options (array): An array of option objects, each containing a `key` and `label`.
 *   - selectedOptions (array): An array of selected option keys.
 *   - intl (object): The internationalization object provided by react-intl's `injectIntl`.
 *   - showNotSelectedItems (bool): Flag to show or hide non-selected items.
 *
 * Each option is rendered as an `ItemChip` component, which is responsible for the actual display
 * of the chip.
 *
 * @param {object} props
 * @param {Array<{ key: string, label: string }>} props.options
 * @param {Array<string>} props.selectedOptions
 * @param {boolean} props.showNotSelectedItems
 * @returns {React.JSX.Element}
 */
const ActivitiesPropertyGroupComponent = ({
  options,
  selectedOptions = [],
  showNotSelectedItems = false,
}) => {
  const intl = useIntl();
  return (
    <Box>
      {options.map((option) => (
        <ItemChip
          icon={getIcon(option.key)}
          isSelected={selectedOptions.includes(option.key)}
          key={`${option.key}`}
          label={intl.formatMessage({ id: option.label })}
          showNotSelectedItems={showNotSelectedItems}
        />
      ))}
    </Box>
  );
};

ActivitiesPropertyGroupComponent.displayName = 'ActivitiesPropertyGroup';
const ActivitiesPropertyGroup = memo(ActivitiesPropertyGroupComponent);

export default ActivitiesPropertyGroup;
